import { useMemo } from "react";
import { useOnboardingResult } from "common/hooks";

import { useDarkMode } from "@/hooks/useDarkMode";
import type { Photo } from "common/apis/onboarding";

import { CMS_EMPTY_VALUE } from "./constants";

export interface LessonStep {
  id: number;
  imageUrl: string;
  title: string | null;
  body: string | null;
  /** The artwork's accessible name. */
  altText: string | null;
}

const cmsText = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === CMS_EMPTY_VALUE) return null;
  return trimmed;
};

/**
 * One lesson's steps from its CMS album, the same reading native makes
 * (`features/learning-hub/useLessonSteps.ts`): ordered by `position`, rows
 * without artwork dropped, `description` as body and `alt_text` as the
 * picture's accessible name.
 */
export function useLessonSteps(albumName?: string): {
  steps: LessonStep[];
  isLoading: boolean;
} {
  const isDarkMode = useDarkMode();
  const { data: album, isLoading } = useOnboardingResult({
    isMobile: false,
    isDark: isDarkMode,
    albumName,
  });

  const steps = useMemo<LessonStep[]>(() => {
    if (!albumName) return [];
    return [...(album?.photos ?? [])]
      .sort((a: Photo, b: Photo) => a.position - b.position)
      .filter((photo: Photo) => !!photo.image_url)
      .map((photo: Photo) => ({
        id: photo.id,
        imageUrl: photo.image_url as string,
        title: cmsText(photo.title),
        body: cmsText(photo.description),
        altText: cmsText(photo.alt_text),
      }));
  }, [album, albumName]);

  return { steps, isLoading: !!albumName && isLoading };
}
